import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react'
import { api } from '../lib/api'
import { Store } from '../types'
import { getCacheStale, setCache } from '../lib/cache'
import { calculateStoreIsOpen } from '../lib/storeUtils'
import { showOfflineToast } from '../lib/toastManager'

interface StoreDetailContextType {
  getStore: (storeId: string) => Promise<Store | null>
  getStoreSync: (storeId: string) => Store | null
  clearStore: (storeId: string) => void
  clearAllStores: () => void
}

const StoreDetailContext = createContext<StoreDetailContextType | undefined>(undefined)

interface StoreDetailProviderProps {
  children: ReactNode
}

// Cache en memoria para stores cargados (más rápido que localStorage)
const storeCache = new Map<string, { store: Store; timestamp: number }>()

// Protección contra llamadas simultáneas al mismo storeId
const loadingStores = new Map<string, Promise<Store | null>>()

// TTL para cache en memoria (30 minutos)
const MEMORY_CACHE_TTL = 30 * 60 * 1000

export const StoreDetailProvider: React.FC<StoreDetailProviderProps> = ({ children }) => {
  /**
   * Obtiene un store desde cache o API
   * @param storeId - ID del store a cargar
   * @returns Store o null si hay error
   */
  const getStore = useCallback(async (storeId: string): Promise<Store | null> => {
    if (!storeId) return null

    const cacheKey = `store_detail_${storeId}`

    // 1. Verificar cache en memoria primero (más rápido)
    const memoryCached = storeCache.get(storeId)
    if (memoryCached && Date.now() - memoryCached.timestamp < MEMORY_CACHE_TTL) {
      return memoryCached.store
    }

    // 2. Verificar cache en localStorage
    const cachedStore = getCacheStale<Store>(cacheKey)
    if (cachedStore) {
      // Actualizar cache en memoria
      storeCache.set(storeId, { store: cachedStore, timestamp: Date.now() })
      return cachedStore
    }

    // 3. Si ya hay una petición en curso para este store, esperar a que termine
    // Esto previene queries duplicadas incluso con React StrictMode
    const existingPromise = loadingStores.get(storeId)
    if (existingPromise) {
      return existingPromise
    }

    // 4. Crear nueva petición
    const promise = (async () => {
      try {
        const response = await api.get(`/stores/${storeId}`)
        if (response.data.success && response.data.data) {
          const storeData = response.data.data

          // Mapear campos del backend al formato del frontend
          const mappedStore: Store = {
            id: storeData.id,
            name: storeData.name,
            slug: storeData.slug,
            description: storeData.description,
            address: storeData.address,
            phone: storeData.phone,
            email: storeData.email || '',
            logoUrl: storeData.logo_url,
            coverImageUrl: storeData.cover_image_url,
            latitude: storeData.latitude,
            longitude: storeData.longitude,
            status: storeData.status,
            // Calcular isActive basándose en operating_hours
            isActive: calculateStoreIsOpen(
              storeData.operating_hours,
              storeData.is_active !== false && storeData.is_active !== null
            ),
            offersKitchenService: storeData.offers_kitchen_service || false,
            offersDelivery: storeData.offers_delivery !== false,
            deliveryFee: Number(storeData.delivery_fee) || 0,
            minOrderAmount: Number(storeData.min_order_amount) || 0,
            deliveryTime: Number(storeData.delivery_time) || 30,
            operatingHours: storeData.operating_hours,
            chargesItbis: storeData.charges_itbis !== false,
            ownerId: storeData.owner_id,
            rating: storeData.rating ? Number(storeData.rating) : null,
            reviewCount: storeData.review_count ? Number(storeData.review_count) : 0,
            categories: storeData.categories || []
          }

          // Guardar en ambos caches
          storeCache.set(storeId, { store: mappedStore, timestamp: Date.now() })
          setCache(cacheKey, mappedStore, 30 * 60 * 1000) // 30 minutos en localStorage

          return mappedStore
        }
      } catch (error: any) {
        console.error('Error loading store:', error)

        // Si hay error de red y tenemos datos en cache, retornarlos
        if ((error.code === 'ERR_NETWORK' || error.code === 'ECONNABORTED' || !error.response) && cachedStore) {
          showOfflineToast()
          return cachedStore
        }

        // Si hay error pero tenemos cache en memoria, retornarlo
        if (memoryCached) {
          return memoryCached.store
        }

        return null
      } finally {
        // Limpiar la promesa de carga cuando termine (exitoso o con error)
        loadingStores.delete(storeId)
      }

      return null
    })()

    // Guardar la promesa para que otras llamadas simultáneas la reutilicen
    loadingStores.set(storeId, promise)
    return promise
  }, [])

  /**
   * Obtiene un store sincrónicamente desde cache (sin hacer API call)
   * Útil cuando ya sabemos que el store está cargado
   */
  const getStoreSync = useCallback((storeId: string): Store | null => {
    if (!storeId) return null

    // Verificar cache en memoria
    const memoryCached = storeCache.get(storeId)
    if (memoryCached && Date.now() - memoryCached.timestamp < MEMORY_CACHE_TTL) {
      return memoryCached.store
    }

    // Verificar cache en localStorage
    const cacheKey = `store_detail_${storeId}`
    const cachedStore = getCacheStale<Store>(cacheKey)
    if (cachedStore) {
      // Actualizar cache en memoria
      storeCache.set(storeId, { store: cachedStore, timestamp: Date.now() })
      return cachedStore
    }

    return null
  }, [])

  /**
   * Limpia el cache de un store específico
   */
  const clearStore = useCallback((storeId: string) => {
    storeCache.delete(storeId)
    const cacheKey = `store_detail_${storeId}`
    try {
      localStorage.removeItem(`colmafacil_cache_${cacheKey}`)
    } catch (error) {
      console.warn('Error clearing store cache:', error)
    }
  }, [])

  /**
   * Limpia todo el cache de stores
   */
  const clearAllStores = useCallback(() => {
    storeCache.clear()
    // Limpiar todos los stores del localStorage
    try {
      const keys = Object.keys(localStorage)
      keys.forEach(key => {
        if (key.startsWith('colmafacil_cache_store_detail_')) {
          localStorage.removeItem(key)
        }
      })
    } catch (error) {
      console.warn('Error clearing all stores cache:', error)
    }
  }, [])

  const value: StoreDetailContextType = {
    getStore,
    getStoreSync,
    clearStore,
    clearAllStores
  }

  return (
    <StoreDetailContext.Provider value={value}>
      {children}
    </StoreDetailContext.Provider>
  )
}

export const useStoreDetail = () => {
  const context = useContext(StoreDetailContext)
  if (context === undefined) {
    // Retornar valores por defecto si el contexto no está disponible
    console.warn('useStoreDetail called outside StoreDetailProvider, using default values')
    return {
      getStore: async () => null,
      getStoreSync: () => null,
      clearStore: () => {},
      clearAllStores: () => {}
    }
  }
  return context
}

